import {
  createContext,
  FunctionComponent,
  PropsWithChildren,
  ReactNode,
  useContext,
  useMemo,
  useState,
} from "react";

type MountContextType = {
  push: (item: { element: ReactNode }) => string;
  close: (id: string) => void;
};

type MountItemContextType = {
  close: () => void;
};

const MountContext = createContext<MountContextType | undefined>(undefined);
const MountItemContext = createContext<MountItemContextType | undefined>(
  undefined
);

export const Mount: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const [stack, setStack] = useState<
    { id: string; element: ReactNode; ctx: MountItemContextType }[]
  >([]);

  const ctxValue = useMemo<MountContextType>(
    () => ({
      push: (item) => {
        const id = Math.random().toString(36).substring(2, 16);
        setStack((prev) => [
          ...prev,
          {
            id,
            element: item.element,
            ctx: {
              close: () => {
                setStack((prev) => prev.filter((i) => i.id !== id));
              },
            },
          },
        ]);
        return id;
      },
      close: (id) => {
        setStack((prev) => prev.filter((i) => i.id !== id));
      },
    }),
    []
  );

  return (
    <MountContext.Provider value={ctxValue}>
      {children}
      <div>
        {stack.map((item) => (
          <MountItemContext.Provider key={item.id} value={item.ctx}>
            <div>{item.element}</div>
          </MountItemContext.Provider>
        ))}
      </div>
    </MountContext.Provider>
  );
};

export const useMountContext = () => {
  const context = useContext(MountContext);
  if (!context) {
    throw new Error("useMountContext must be used within a MountProvider");
  }
  return context;
};

export const useMountItemContext = () => {
  const context = useContext(MountItemContext);
  if (!context) {
    throw new Error(
      "useMountItemContext must be used within a MountItemProvider"
    );
  }
  return context;
};
